/**
 * **One client's self-booking exception** (booking-system R2) — the three states the client card's
 * „Ustawienia rozliczeń" dialog offers, what each one says, and the write it sends.
 *
 * The wire keeps an override in either direction or none (`overrideAllowed`: `true`, `false`,
 * `null`), so the three states are the wire's own: following the general setting, allowed, and
 * refused. „Jak w ustawieniach ogólnych" is not the same as the state that happens to agree with
 * the general setting today — an explicit „Pozwól" still holds after the trainer turns self-booking
 * off for everyone, which is exactly what the mobile app's notes say. So the general state is a
 * DELETE, and the other two are a PUT, whatever the general setting reads now.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

export type SelfBookingChoice = 'general' | 'allow' | 'refuse';

/** The general setting first, then the two exceptions — the refusal before the allowance. */
export const SELF_BOOKING_CHOICES: readonly SelfBookingChoice[] = ['general', 'refuse', 'allow'];

/** The state the wire's override names. */
export function selfBookingChoiceOf(overrideAllowed: boolean | null): SelfBookingChoice {
  if (overrideAllowed === null) return 'general';
  return overrideAllowed ? 'allow' : 'refuse';
}

export type SelfBookingWrite =
  | { readonly kind: 'none' }
  | { readonly kind: 'follow' }
  | { readonly kind: 'set'; readonly allowed: boolean };

/** What saving `chosen` over the `current` state sends — nothing when nothing changed. */
export function selfBookingWrite(
  current: SelfBookingChoice,
  chosen: SelfBookingChoice,
): SelfBookingWrite {
  if (current === chosen) return { kind: 'none' };
  if (chosen === 'general') return { kind: 'follow' };
  return { kind: 'set', allowed: chosen === 'allow' };
}

/**
 * What the chosen state does for this client, said against the general setting as it reads now:
 * following it is „like all your clients", an exception holds whichever way the general setting
 * later moves.
 */
export function selfBookingEffectKey(choice: SelfBookingChoice, generalOn: boolean): string {
  const effect = choice === 'general' ? (generalOn ? 'generalOn' : 'generalOff') : choice;
  return `web:clientDetail.selfBooking.effect.${effect}`;
}
